import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Table,
  Badge,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Pagination,
  PaginationItem,
  PaginationLink,
  InputGroup,
  Collapse
} from 'reactstrap';
import { FaHistory, FaSearch, FaFilter, FaChevronDown, FaChevronUp, FaUser, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import api from '../utils/api';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(50);
  const [totalLogs, setTotalLogs] = useState(0);

  // Filters
  const [filters, setFilters] = useState({
    scope: '',
    action: '',
    resource_type: '',
    success: '',
    showFilters: false
  });

  useEffect(() => {
    loadLogs();
    loadStats();
  }, [currentPage, filters.scope, filters.action, filters.resource_type, filters.success]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams({
        page: currentPage,
        per_page: perPage
      });

      if (filters.scope) params.append('scope', filters.scope);
      if (filters.action) params.append('action', filters.action);
      if (filters.resource_type) params.append('resource_type', filters.resource_type);
      if (filters.success) params.append('success', filters.success);

      const response = await api.get(`/audit-logs?${params.toString()}`);
      setLogs(response.data.logs);
      setTotalPages(response.data.pagination.pages);
      setTotalLogs(response.data.pagination.total);
    } catch (err) {
      if (err.response && err.response.status !== 404) {
        setError('Failed to load audit logs');
      }
      console.error('Load audit logs error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/audit-logs/stats');
      setStats(response.data);
    } catch (err) {
      console.error('Load stats error:', err);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    return dateString ? new Date(dateString).toLocaleString() : '-';
  };

  const getActionBadgeClass = (success) => {
    return success ? 'bg-dark text-success border border-success' : 'bg-dark text-danger border border-danger';
  };

  const getResourceTypeColor = (resourceType) => {
    const colors = {
      'User': 'bg-dark text-primary border border-primary',
      'Order': 'bg-dark text-warning border border-warning',
      'Source': 'bg-dark text-info border border-info',
      'EppAccount': 'bg-dark text-secondary border border-secondary',
      'EppCategory': 'bg-dark text-secondary border border-secondary'
    };
    return colors[resourceType] || 'bg-dark text-light border border-secondary';
  };

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="page-heading">
              <FaHistory className="page-heading-icon" />
              Audit Logs
            </h2>
          </div>
        </Col>
      </Row>

      {error && <Alert color="danger" dismissible onDismiss={() => setError('')}>{error}</Alert>}

      {/* Statistics Cards */}
      <Row className="mb-4">
        <Col md="3" className="mb-3">
          <Card>
            <CardBody>
              <CardTitle tag="h6" className="text-muted mb-1">Total Logs</CardTitle>
              <h3 className="mb-0">{stats.total_logs || 0}</h3>
            </CardBody>
          </Card>
        </Col>
        <Col md="3" className="mb-3">
          <Card>
            <CardBody>
              <CardTitle tag="h6" className="text-muted mb-1">
                <FaCheckCircle className="me-1 text-success" />
                Success
              </CardTitle>
              <h3 className="mb-0 text-success">{stats.success_count || 0}</h3>
            </CardBody>
          </Card>
        </Col>
        <Col md="3" className="mb-3">
          <Card>
            <CardBody>
              <CardTitle tag="h6" className="text-muted mb-1">
                <FaExclamationTriangle className="me-1 text-danger" />
                Failures
              </CardTitle>
              <h3 className="mb-0 text-danger">{stats.failure_count || 0}</h3>
            </CardBody>
          </Card>
        </Col>
        <Col md="3" className="mb-3">
          <Card>
            <CardBody>
              <CardTitle tag="h6" className="text-muted mb-1">Success Rate</CardTitle>
              <h3 className="mb-0">
                {stats.total_logs ? Math.round((stats.success_count / stats.total_logs) * 100) : 0}%
              </h3>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="mb-4">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center">
            <h5>
              <FaFilter className="me-2" />
              Filters
            </h5>
            <Button
              color="link"
              onClick={() => handleFilterChange('showFilters', !filters.showFilters)}
              className="p-0"
            >
              {filters.showFilters ? <FaChevronUp /> : <FaChevronDown />}
            </Button>
          </div>
          <Collapse isOpen={filters.showFilters}>
            <hr />
            <Row>
              <Col md="3">
                <FormGroup>
                  <Label>Action</Label>
                  <InputGroup>
                    <Input
                      type="text"
                      placeholder="Search actions..."
                      value={filters.action}
                      onChange={(e) => handleFilterChange('action', e.target.value)}
                      className="bg-secondary border-secondary text-white"
                    />
                    <Button color="primary">
                      <FaSearch />
                    </Button>
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label>Resource Type</Label>
                  <Input
                    type="select"
                    value={filters.resource_type}
                    onChange={(e) => handleFilterChange('resource_type', e.target.value)}
                    className="bg-secondary border-secondary text-white form-control"
                  >
                    <option value="">All Resources</option>
                    <option value="User">User</option>
                    <option value="Order">Order</option>
                    <option value="Source">Source</option>
                    <option value="EppAccount">EPP Account</option>
                    <option value="EppCategory">EPP Category</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label>Status</Label>
                  <Input
                    type="select"
                    value={filters.success}
                    onChange={(e) => handleFilterChange('success', e.target.value)}
                    className="bg-secondary border-secondary text-white form-control"
                  >
                    <option value="">All</option>
                    <option value="true">Success</option>
                    <option value="false">Failure</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label>&nbsp;</Label>
                  <div>
                    <Button
                      color="secondary"
                      onClick={() => {
                        setFilters({ scope: '', action: '', resource_type: '', success: '', showFilters: true });
                        setCurrentPage(1);
                      }}
                    >
                      Clear Filters
                    </Button>
                  </div>
                </FormGroup>
              </Col>
            </Row>
          </Collapse>
        </CardBody>
      </Card>

      {/* Audit Logs Table */}
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>Audit Logs ({totalLogs})</h5>
          </div>

          {loading ? (
            <div className="text-center py-4">Loading audit logs...</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-4">No audit logs found</div>
          ) : (
            <>
              <div className="table-responsive">
                <Table striped>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>User</th>
                      <th>Action</th>
                      <th>Resource</th>
                      <th>Status</th>
                      <th>IP Address</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log => (
                      <tr key={log.id}>
                        <td>{formatDate(log.created_at)}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <FaUser className="me-2 text-muted" size={14} />
                            {log.actor || `ID: ${log.actor_id}`}
                          </div>
                        </td>
                        <td>
                          <Badge className="bg-dark text-primary border border-primary">
                            {log.action}
                          </Badge>
                        </td>
                        <td>
                          {log.resource_type && (
                            <div>
                              <Badge className={getResourceTypeColor(log.resource_type)}>
                                {log.resource_type}
                              </Badge>
                              {log.resource_id && (
                                <div className="text-muted small">ID: {log.resource_id}</div>
                              )}
                            </div>
                          )}
                        </td>
                        <td>
                          <Badge className={getActionBadgeClass(log.success)}>
                            {log.success ? 'Success' : 'Failed'}
                          </Badge>
                        </td>
                        <td className="text-muted small">{log.ip_address || '-'}</td>
                        <td>
                          {log.error_message && (
                            <div className="text-danger small">{log.error_message}</div>
                          )}
                          {log.details && Object.keys(log.details).length > 0 && (
                            <div className="text-muted small">
                              {Object.entries(log.details).slice(0, 2).map(([key, value]) => (
                                <div key={key}>{key}: {JSON.stringify(value)}</div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <PaginationItem disabled={currentPage <= 1}>
                      <PaginationLink previous onClick={() => setCurrentPage(currentPage - 1)} />
                    </PaginationItem>
                    {[...Array(totalPages)].map((_, i) => (
                      <PaginationItem key={i} active={currentPage === i + 1}>
                        <PaginationLink onClick={() => setCurrentPage(i + 1)}>
                          {i + 1}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                    <PaginationItem disabled={currentPage >= totalPages}>
                      <PaginationLink next onClick={() => setCurrentPage(currentPage + 1)} />
                    </PaginationItem>
                  </Pagination>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </Container>
  );
};

export default AuditLogs;
